import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Logo from '../components/Logo';
import { ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/invite/${token}`);
        if (alive) setInvite(data);
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || 'Invite is invalid or expired.');
      }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setError('');
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/accept-invite`, { token, password }, { withCredentials: true });
      toast.success('Account activated — welcome!');
      await checkAuth();
      const role = data.role;
      const adminRoles = ['admin', 'super_admin', 'manager', 'service_manager', 'construction_manager', 'office_staff'];
      if (adminRoles.includes(role)) navigate('/admin/dashboard');
      else if (role === 'employee' || role === 'technician') navigate('/employee/timeclock');
      else if (role === 'vip') navigate('/vip/dashboard');
      else navigate('/customer/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not accept invite');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8 flex flex-col items-center">
          <Logo size="lg" />
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] mt-4 mb-2">ACCEPT INVITE</p>
        </div>
        <div className="bg-[#141414] border border-[#262626] rounded-md p-8">
          {loading ? (
            <p data-testid="invite-loading" className="text-[#A3A3A3] text-center">Loading invite…</p>
          ) : !invite ? (
            <div data-testid="invite-invalid" className="space-y-4 text-center">
              <p className="text-red-400 text-sm">{error || 'Invite not found.'}</p>
              <Link to="/login" className="text-[#D4AF37] text-sm hover:text-[#F3E5AB]">← Back to sign in</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <div className="text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center mb-3">
                  <ShieldCheck className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <p className="text-white font-medium">Welcome, {invite.name}</p>
                <p className="text-[#A3A3A3] text-sm">You've been invited to Nexflow Plumbing as a <span className="text-[#D4AF37]">{invite.role}</span>.</p>
              </div>
              {error && <div data-testid="invite-error" className="bg-[#7F1D1D] text-white p-3 rounded-md text-sm">{error}</div>}
              <div>
                <Label className="text-white">Email</Label>
                <Input value={invite.email} readOnly className="bg-[#0A0A0A] border-[#262626] text-[#A3A3A3]" />
              </div>
              <div>
                <Label className="text-white">Choose a password</Label>
                <Input data-testid="invite-password" type="password" minLength={8} required value={password} onChange={(e) => setPassword(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
              <div>
                <Label className="text-white">Confirm password</Label>
                <Input data-testid="invite-confirm" type="password" minLength={8} required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
              <Button data-testid="invite-accept-submit" type="submit" disabled={submitting} className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
                {submitting ? 'Activating…' : 'Activate Account'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AcceptInvite;
